import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";

const services = [
  { to: "/bokforing", label: "Löpande bokföring" },
  { to: "/bokslut", label: "Bokslut & deklaration" },
  { to: "/radgivning", label: "Rådgivning & tillväxt" },
  { to: "/inhouse", label: "Inhouse-medarbetare" },
  { to: "/loneadministration", label: "Löneadministration" },
];

const contacts = [
  { to: "/kontakt-umea", label: "Umeå" },
  { to: "/kontakt-stockholm", label: "Stockholm" },
  { to: "/medarbetare", label: "Medarbetare" },
];

const primary = [
  { to: "/karriar", label: "Karriär" },
  { to: "/kundreferenser", label: "Referenser" },
  { to: "/nyheter", label: "Nyheter" },
  { to: "/om-oss", label: "Om oss" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isHeroMode = isHome && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        isHeroMode
          ? "bg-transparent"
          : "bg-background/85 backdrop-blur-xl border-b border-border"
      }`}
    >
      <div className="container-page flex h-20 items-center justify-between">
        <Logo tone={isHeroMode ? "white" : "default"} />

        <nav className="hidden lg:flex items-center gap-1">
          <DropdownNav label="Tjänster" items={services} heroMode={isHeroMode} />
          {primary.map((p) => (
            <NavLink
              key={p.to}
              to={p.to}
              className={({ isActive }) =>
                `px-4 py-2 text-sm transition-colors ${
                  isActive
                    ? "text-primary"
                    : isHeroMode
                      ? "text-background/85 hover:text-background"
                      : "text-foreground/80 hover:text-foreground"
                }`
              }
            >
              {p.label}
            </NavLink>
          ))}
          <DropdownNav label="Kontakt" items={contacts} heroMode={isHeroMode} />
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/kontakt-umea"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${
              isHeroMode
                ? "border border-background/35 bg-background/10 text-background backdrop-blur-md hover:bg-background/18"
                : "border border-primary/20 bg-primary/8 text-primary shadow-[0_10px_24px_-18px_rgba(31,168,232,0.9)] hover:border-primary/35 hover:bg-primary/12"
            }`}
          >
            Boka möte
          </Link>
          <a
            href="https://portal.cloudredovisning.se/"
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${
              isHeroMode
                ? "bg-background text-accent shadow-[0_14px_34px_-18px_rgba(255,255,255,0.7)] hover:bg-background/92"
                : "bg-primary text-primary-foreground shadow-[0_10px_24px_-18px_rgba(31,168,232,0.9)] hover:bg-foreground"
            }`}
          >
            Kundportal <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>

        <button
          aria-label="Meny"
          onClick={() => setOpen((s) => !s)}
          className={`lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-sm border transition-colors ${
            isHeroMode
              ? "border-background/25 bg-background/10 text-background backdrop-blur-md"
              : "border-border text-foreground"
          }`}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white/95 backdrop-blur-xl">
          <div className="container-page py-6 flex flex-col gap-1">
            <MobileGroup title="Tjänster" items={services} />
            {primary.map((p) => (
              <NavLink
                key={p.to}
                to={p.to}
                className={({ isActive }) =>
                  `py-3 border-b border-border/50 ${isActive ? "text-primary" : "text-foreground"}`
                }
              >
                {p.label}
              </NavLink>
            ))}
            <MobileGroup title="Kontakt" items={contacts} />
            <Link
              to="/kontakt-umea"
              className="mt-4 inline-flex items-center justify-center rounded-full border border-primary/20 bg-primary/8 px-5 py-3 font-medium text-primary shadow-[0_10px_24px_-18px_rgba(31,168,232,0.9)]"
            >
              Boka möte
            </Link>
            <a
              href="https://portal.cloudredovisning.se/"
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 font-medium text-primary-foreground"
            >
              Kundportal <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const DropdownNav = ({
  label,
  items,
  heroMode = false,
}: {
  label: string;
  items: { to: string; label: string }[];
  heroMode?: boolean;
}) => (
  <div className="relative group">
    <button
      className={`inline-flex items-center gap-1 px-4 py-2 text-sm transition-colors ${
        heroMode
          ? "text-background/85 group-hover:text-background"
          : "text-foreground/80 group-hover:text-foreground"
      }`}
    >
      {label}
      <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
    </button>
    <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0">
      <div className="min-w-[260px] rounded-lg border border-border bg-white p-2 shadow-elevated">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) =>
              `block rounded-md px-3 py-2.5 text-sm transition-colors ${
                isActive ? "bg-secondary text-primary" : "text-foreground/85 hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            {it.label}
          </NavLink>
        ))}
      </div>
    </div>
  </div>
);

const MobileGroup = ({ title, items }: { title: string; items: { to: string; label: string }[] }) => (
  <div className="py-2 border-b border-border/50">
    <div className="text-xs uppercase tracking-[0.2em] text-foreground-muted mb-2">{title}</div>
    <div className="flex flex-col">
      {items.map((it) => (
        <Link key={it.to} to={it.to} className="py-2 text-foreground/85">
          {it.label}
        </Link>
      ))}
    </div>
  </div>
);
